/* ----------------------------------------------------------------------------------------------------------------------
   SWATCH-SWAP PDP COMPONENT — core swap + real product-gallery + fixed-shell size/inventory buy-box

   Generic edition-swap mechanism for any tag-grouped product line assigned templates/product.swatch-swap.json
   (sections/swatch-swap-product.liquid). Not BBCOR-specific — see _shared/WARSTIC_BBCOR_STATUS.md for the full
   plan (video support still deferred, "Phase 3") and the confirmed size-preservation rule implemented below.

   2026-07-28 UAT round 3: add-to-cart no longer does its own /cart/add.js fetch. snippets/swatch-swap-buybox.liquid
   now renders the theme's real 'buy-buttons' snippet (native <product-form>/<buy-buttons> custom elements,
   assets/theme.js) — the same AJAX submission, cart-drawer integration, and "Buy with Shop" dynamic checkout
   button every other PDP on this site already gets. This file only updates that real form's hidden variant id
   input and the real submit button's disabled state/label when the customer changes size or edition.

   2026-07-28 UAT round 5: the gallery is no longer built or swapped by this file at all. Every sibling edition
   now gets its own real, fully server-rendered product-gallery instance (sections/swatch-swap-product.liquid),
   toggled via the native `hidden` attribute on edition swap — swapGallery() below. Gets zoom, mobile dot
   pagination, and correct thumbnail layout from Prestige's own proven component, not reimplemented here. See
   swapGallery()'s comment for the hidden-init risk verification (read, not assumed) that justified this.
   ---------------------------------------------------------------------------------------------------------------------- */
(function () {
  var SIZE_CHANGED_CUE_MS = 700;
  var matrixCache = new WeakMap();
  var pristineVideoHtml = new WeakMap();

  function init() {
    capturePristineVideos();
    document.addEventListener('change', onEditionChange);
    document.addEventListener('click', onSizeButtonClick);
  }

  // 2026-07-30: captured once at load, before any click is physically possible, so resetHiddenVideos()
  // (below) always has a genuinely pre-play snapshot to restore — video-media has no "unload" API of its
  // own (see that function's comment), so this is the only way back to a fresh, replayable instance.
  function capturePristineVideos() {
    document.querySelectorAll('[data-swatch-swap-gallery] video-media').forEach(function (videoMedia) {
      var parent = videoMedia.parentElement;
      if (parent) pristineVideoHtml.set(parent, videoMedia.outerHTML);
    });
  }

  function getRoot(el) {
    return el.closest('[data-swatch-swap-root]');
  }

  function getMatrix(rootEl) {
    if (matrixCache.has(rootEl)) return matrixCache.get(rootEl);

    var script = rootEl.querySelector('[data-swatch-swap-matrix]');
    var matrix = {};

    if (script) {
      try {
        matrix = JSON.parse(script.textContent);
      } catch (e) {
        matrix = {};
      }
    }

    matrixCache.set(rootEl, matrix);
    return matrix;
  }

  function getSelectedSizeButton(rootEl) {
    return rootEl.querySelector('[data-swatch-swap-size-button].is-selected');
  }

  /* ---------------------------------------------------------------------------------------------------------------
     EDITION SWAP
     --------------------------------------------------------------------------------------------------------------- */

  function onEditionChange(event) {
    var input = event.target;
    if (!input.matches('[data-swatch-swap-editions] input[type="radio"]')) return;

    var rootEl = getRoot(input);
    if (!rootEl) return;

    var handle = input.getAttribute('data-product-handle');
    var matrix = getMatrix(rootEl);
    var edition = matrix[handle];
    if (!edition) return;

    var previousButton = getSelectedSizeButton(rootEl);
    var previousSize = previousButton ? previousButton.getAttribute('data-size') : null;

    swapGallery(rootEl, handle);
    swapTitle(rootEl, edition);
    swapDescription(rootEl, edition);
    syncStickyBar(rootEl, edition);
    var sizeFallbackAnnounced = rebuildSizeButtons(rootEl, edition, previousSize);
    updateUrl(input);

    // The size-fallback announcement (when the previous size wasn't available on the new edition) already
    // names the edition, so it takes priority over this generic one rather than being immediately overwritten
    // by it.
    if (!sizeFallbackAnnounced) {
      announce(rootEl, 'Now viewing ' + edition.title);
    }
  }

  function announce(rootEl, message) {
    var region = rootEl.querySelector('[data-swatch-swap-announcer]');
    if (!region) return;

    // Clear first, then set on the next frame — screen readers don't reliably re-announce an aria-live
    // region whose text content is set to the same string it already holds.
    region.textContent = '';
    window.requestAnimationFrame(function () {
      region.textContent = message;
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     GALLERY

     2026-07-28 UAT round 5: rounds 1-2 hand-built a custom main-image + thumbnail-strip here, patched twice for
     a recurring srcset bug, and still couldn't produce zoom, mobile dot pagination, or the correct desktop
     thumbnail layout — those aren't separable features, they're all part of Prestige's real product-gallery
     component. Every sibling edition now gets its own real, pre-rendered product-gallery instance
     (sections/swatch-swap-product.liquid); this just toggles which one is visible.

     Hidden-init risk, verified by reading assets/theme.js's ScrollCarousel before building this (not assumed):
     select() gates its real scrollTo positioning behind a visibility check and just records the target index
     while hidden; a ResizeObserver on the carousel (always active) fires the moment a hidden-to-visible
     transition changes its size, re-running that positioning with real dimensions via onResize_fn's correction
     pass. Toggling the `hidden` attribute on a wrapping div (not the carousel element itself) is sufficient —
     ResizeObserver reports real size changes regardless of which ancestor caused them.
     --------------------------------------------------------------------------------------------------------------- */

  function swapGallery(rootEl, handle) {
    var mediaWrap = rootEl.querySelector('[data-swatch-swap-media]');
    if (!mediaWrap) return;

    mediaWrap.querySelectorAll('[data-swatch-swap-gallery]').forEach(function (gallery) {
      var isHiding = gallery.getAttribute('data-product-handle') !== handle;
      gallery.hidden = isHiding;
      if (isHiding) resetHiddenVideos(gallery);
    });
  }

  // 2026-07-30: the `hidden` attribute above is a display:none toggle only — it does not stop a playing
  // <video-media> (native <video>, YouTube, or Vimeo, assets/theme.js), which keeps running with audible
  // audio in the background on swap. .pause() (BaseMedia) handles all three backends uniformly and silences
  // it immediately. Per Jake's decision, swapping back to this edition later should show the poster/
  // play-button state again, not resume mid-frame — but video-media marks itself `loaded` permanently on
  // first play and its click-to-play listener is one-shot (`{ once: true }`), so removing that attribute
  // alone would leave a visually-reset but unclickable element. Replacing it with a clone of its own
  // pristine (pre-play) markup gives a genuinely fresh, replayable instance instead. Only the <video-media>
  // tag is replaced, not its parent `.product-gallery__media` slide, so the carousel's own cell tracking
  // (verified safe under hidden-attribute toggling for round 5's gallery swap) is untouched.
  function resetHiddenVideos(gallery) {
    gallery.querySelectorAll('video-media[loaded], video-media[playing]').forEach(function (videoMedia) {
      if (typeof videoMedia.pause === 'function') videoMedia.pause();

      var parent = videoMedia.parentElement;
      var pristineHtml = parent && pristineVideoHtml.get(parent);
      if (!pristineHtml) return;

      var template = document.createElement('template');
      template.innerHTML = pristineHtml;
      videoMedia.replaceWith(template.content.firstElementChild);
    });
  }

  function swapTitle(rootEl, edition) {
    var title = rootEl.querySelector('[data-swatch-swap-title]');
    if (title && edition.title) title.textContent = edition.title;
  }

  // 2026-07-28 UAT round 3: description block was showing the originally-loaded product's description on
  // every edition, since each edition is genuinely a separate product with its own description field and
  // nothing was re-rendering it on swap. Container can be in accordion mode (content lives in a nested
  // .accordion__content, from snippets/accordion.liquid, read not modified) or plain-div mode (the wrapper's
  // own innerHTML) — handles both without needing to know which one is active.
  function swapDescription(rootEl, edition) {
    var wrapper = rootEl.querySelector('[data-swatch-swap-description]');
    if (!wrapper || edition.description == null) return;

    var accordionContent = wrapper.querySelector('.accordion__content');
    if (accordionContent) {
      accordionContent.innerHTML = edition.description;
    } else {
      wrapper.innerHTML = edition.description;
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     STICKY ADD-TO-CART BAR

     2026-07-28 UAT round 4: reuses main-product.liquid's own <product-sticky-bar> element and show/hide
     behavior verbatim (assets/theme.js — pure scroll/viewport logic via IntersectionObserver, no dependency on
     main-product.liquid's specific structure beyond a findable form and a footer section, both present here
     too). Its "Add to Cart" button submits through the exact same real form as the main buy box
     (form="..." attribute, no separate hidden id input needed) — updateAddToCartTarget() below keeps both
     submit buttons in sync from one place. Only the thumbnail/title/price content needs swap-driven sync here,
     since that's the part with no native cross-product update path.
     --------------------------------------------------------------------------------------------------------------- */

  function syncStickyBar(rootEl, edition) {
    var stickyBar = rootEl.querySelector('[data-swatch-swap-sticky-bar]');
    if (!stickyBar) return;

    // 2026-07-28 UAT round 5: sourced from the now-visible gallery's own default image rather than a
    // separate JSON media array — the matrix no longer carries one, since the real product-gallery instances
    // render their own images server-side now.
    var imageWrap = stickyBar.querySelector('[data-swatch-swap-sticky-image]');
    var stickyImg = imageWrap && imageWrap.querySelector('img');
    var activeGallery = rootEl.querySelector('[data-swatch-swap-gallery]:not([hidden])');
    var sourceImg = activeGallery && activeGallery.querySelector('.product-gallery__media.is-initial img, .product-gallery__media img');

    if (stickyImg && sourceImg) {
      // Reused element, still carries the srcset it loaded with — same reasoning as every other image-swap
      // fix this build has needed (round 1, round 3): clear it before setting src, or the browser keeps
      // rendering from the stale srcset instead.
      stickyImg.removeAttribute('srcset');
      stickyImg.removeAttribute('sizes');
      stickyImg.src = sourceImg.currentSrc || sourceImg.src;
      stickyImg.alt = sourceImg.alt || '';
    }

    var title = stickyBar.querySelector('[data-swatch-swap-sticky-title]');
    if (title && edition.title) title.textContent = edition.title;
  }

  function updateUrl(input) {
    var url = input.getAttribute('data-product-url');
    if (url && window.history && window.history.pushState) {
      window.history.pushState({}, '', url);
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     SIZE / INVENTORY OVERLAY

     Sold-out sizes render disabled, not hidden — matches this project's existing PDP variant_picker convention
     (hide_sold_out_variants: false), not the collection grid card's hide-if-unavailable convention.

     Size-preservation on swap (confirmed behavior): if the previously-selected size is available on the new
     edition, it stays selected. Otherwise, fall back to the new edition's first available size, with a brief
     visual cue (a short highlight transition via the 'size-changed' class, cleared after SIZE_CHANGED_CUE_MS) —
     no popup or alert.
     --------------------------------------------------------------------------------------------------------------- */

  function rebuildSizeButtons(rootEl, edition, previousSize) {
    var fieldset = rootEl.querySelector('[data-swatch-swap-sizes]');
    if (!fieldset) return;

    // Fixed shell of size slots (2026-07-28 UAT round 2) — every edition always has exactly
    // section.settings.size_shell.length entries here, in the same order, regardless of which sizes this
    // specific edition actually offers. A slot with no matching real variant (id === null) renders disabled
    // exactly like a genuinely sold-out one — the grid's shape never changes between editions. Only a
    // misconfigured (empty) size_shell setting would produce a true empty array here.
    var slots = edition.sizes || [];
    var buttonWrap = fieldset.querySelector('div');
    if (!buttonWrap) return;

    buttonWrap.textContent = '';

    if (slots.length === 0) {
      var soldOut = document.createElement('p');
      soldOut.className = 'swatch-swap-product__sold-out';
      soldOut.textContent = fieldset.getAttribute('data-sold-out-label') || 'Sold out';
      buttonWrap.appendChild(soldOut);
      updatePrice(rootEl, null);
      updateAddToCartTarget(rootEl, null);
      announce(rootEl, 'Now viewing ' + edition.title + ' — sold out in all sizes');
      return true;
    }

    var matchedSlot = null;
    var sizeChanged = false;

    if (previousSize) {
      matchedSlot = slots.filter(function (s) { return s.title === previousSize && s.available; })[0] || null;
    }

    if (!matchedSlot) {
      // Prefer an available slot, then any slot with a real variant behind it (even sold out, so price still
      // reflects a real size), then the shell's first slot as an absolute last resort.
      matchedSlot = slots.filter(function (s) { return s.available; })[0]
        || slots.filter(function (s) { return s.id; })[0]
        || slots[0];
      sizeChanged = !!previousSize;
    }

    slots.forEach(function (slot) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'swatch-swap-size-button';
      button.setAttribute('data-swatch-swap-size-button', '');
      if (slot.id) button.setAttribute('data-variant-id', slot.id);
      button.setAttribute('data-size', slot.title);
      if (slot.price) button.setAttribute('data-price', slot.price);
      if (slot.comparePrice) button.setAttribute('data-compare-price', slot.comparePrice);
      button.textContent = slot.label || slot.title;

      if (!slot.available) {
        button.classList.add('is-disabled');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }

      if (slot === matchedSlot) {
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.setAttribute('aria-pressed', 'false');
      }

      buttonWrap.appendChild(button);
    });

    updatePrice(rootEl, matchedSlot);
    updateAddToCartTarget(rootEl, matchedSlot);

    if (sizeChanged) {
      fieldset.classList.add('size-changed');
      setTimeout(function () {
        fieldset.classList.remove('size-changed');
      }, SIZE_CHANGED_CUE_MS);

      announce(rootEl, 'Selected size not available in ' + edition.title + ' — showing ' + (matchedSlot.label || matchedSlot.title) + ' instead');
      return true;
    }

    return false;
  }

  function onSizeButtonClick(event) {
    var button = event.target.closest('[data-swatch-swap-size-button]');
    if (!button || button.classList.contains('is-disabled')) return;

    var rootEl = getRoot(button);
    if (!rootEl) return;

    var fieldset = button.closest('[data-swatch-swap-sizes]');
    if (fieldset) {
      fieldset.querySelectorAll('[data-swatch-swap-size-button]').forEach(function (btn) {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }

    button.classList.add('is-selected');
    button.setAttribute('aria-pressed', 'true');

    updatePrice(rootEl, {
      price: button.getAttribute('data-price'),
      comparePrice: button.getAttribute('data-compare-price')
    });
    updateAddToCartTarget(rootEl, {
      id: button.getAttribute('data-variant-id'),
      available: true
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     PRICE + ADD TO CART TARGET

     2026-07-28 UAT round 3: updateAddToCartTarget now points at the real form snippets/swatch-swap-buybox.liquid
     renders via the theme's own 'buy-buttons' snippet (render 'buy-buttons', ...), not a custom button. It
     updates the real hidden variant-id input and the real submit button's disabled state/label — actual cart
     submission is handled entirely by Prestige's own <product-form>/<buy-buttons> custom elements
     (assets/theme.js), the same code path every other PDP on the site uses.
     --------------------------------------------------------------------------------------------------------------- */

  function updatePrice(rootEl, variant) {
    // Updates both the main buy box price and the sticky bar's price (2026-07-28 UAT round 4) — they always
    // show the same value at the same time, so one function keeps them trivially in sync rather than risking
    // two independently-maintained copies drifting apart.
    var containers = rootEl.querySelectorAll('[data-swatch-swap-price], [data-swatch-swap-sticky-price]');
    if (!containers.length) return;

    // variant.price is null for a shell slot with no matching real variant on this edition at all (as
    // opposed to a real, sold-out one, which still has a real price) — nothing meaningful to show.
    if (!variant || !variant.price) {
      containers.forEach(function (container) { container.innerHTML = ''; });
      return;
    }

    var onSale = !!variant.comparePrice;

    containers.forEach(function (container) {
      var saleLabel = container.getAttribute('data-sale-price-label') || 'Sale price';
      var regularLabel = container.getAttribute('data-regular-price-label') || 'Regular price';

      var html = '<price-list class="price-list price-list--product">';
      html += '<sale-price class="' + (onSale ? 'h4 text-on-sale' : 'h4 text-subdued') + '">';
      html += '<span class="sr-only">' + saleLabel + '</span>' + variant.price;
      html += '</sale-price>';

      if (onSale) {
        html += '<compare-at-price class="h5 text-subdued line-through">';
        html += '<span class="sr-only">' + regularLabel + '</span>' + variant.comparePrice;
        html += '</compare-at-price>';
      }

      html += '</price-list>';
      container.innerHTML = html;
    });
  }

  function updateAddToCartTarget(rootEl, variant) {
    var wrap = rootEl.querySelector('[data-swatch-swap-buybox-buttons]');
    if (!wrap) return;

    var idInput = wrap.querySelector('input[name="id"]');
    var submitButton = wrap.querySelector('buy-buttons button[type="submit"]');
    var paymentButton = wrap.querySelector('.shopify-payment-button');

    // Sticky bar's "Add to Cart" submits through the same real form (form="..." attribute) as the main buy
    // box's button — no separate hidden id input to update, just its own disabled state/label so it can't be
    // clicked into submitting a sold-out size the main button also just got disabled for.
    var stickySubmitButton = rootEl.querySelector('[data-swatch-swap-sticky-bar] buy-buttons button[type="submit"]');

    var addToCartLabel = wrap.getAttribute('data-add-to-cart-label') || 'Add to cart';
    var soldOutLabel = wrap.getAttribute('data-sold-out-label') || 'Sold out';

    if (!variant || !variant.available) {
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = soldOutLabel;
      }
      if (stickySubmitButton) {
        stickySubmitButton.disabled = true;
        stickySubmitButton.textContent = soldOutLabel;
      }
      if (paymentButton) paymentButton.style.display = 'none';
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = addToCartLabel;
    }

    if (stickySubmitButton) {
      stickySubmitButton.disabled = false;
      stickySubmitButton.textContent = addToCartLabel;
    }

    if (paymentButton) paymentButton.style.display = '';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
